import { useTranslation } from "react-i18next";
import { classNames } from "../../../ui/styles/classNames";
import ItemReferenceName from "../ItemReferenceName";
import { isVersionReference } from "../itemReferences";
import type { FieldChange } from "../../../shared/diff/fieldDiff";
import ChangeRow from "../../../ui/content/ChangeRow";

/** A field-level diff as `path: old → new`, one line per leaf (AU-8). */
export default function FieldChangeList({ changes, className }: { changes: readonly FieldChange[]; className?: string }) {
  const { t } = useTranslation();
  if (changes.length === 0) {
    return <p className={classNames("app-text-muted text-sm", className ?? "")}>{t("authoring.diff.noChanges")}</p>;
  }
  return (
    <ul className={classNames("space-y-0.5 text-sm", className ?? "")} aria-label={t("authoring.diff.fieldChanges")}>
      {changes.map((change) => (
        <ChangeRow key={`${change.kind} ${change.path}`} kind={change.kind}>
          <span className="font-mono text-xs">{change.path === "" ? t("authoring.diff.whole") : change.path}</span>
          {": "}
          {change.kind === "added" ? <FieldValue value={change.after} /> : null}
          {change.kind === "removed" ? <FieldValue value={change.before} /> : null}
          {change.kind === "changed" ? (
            <>
              <FieldValue value={change.before} />
              <span aria-label={t("authoring.diff.becomes")}> → </span>
              <FieldValue value={change.after} />
            </>
          ) : null}
        </ChangeRow>
      ))}
    </ul>
  );
}

/** A leaf: a pinned reference by name, short text as it is, anything else as compact JSON. */
function FieldValue({ value }: { value: unknown }) {
  if (typeof value === "string" && isVersionReference(value)) {
    return <ItemReferenceName reference={value} />;
  }
  const text = typeof value === "string" ? value : JSON.stringify(value);
  const shown = text.length > 160 ? `${text.slice(0, 157)}…` : text;
  return (
    <span className={typeof value === "string" ? "" : "font-mono text-xs"} title={text.length > 160 ? text : undefined}>
      {typeof value === "string" ? `“${shown}”` : shown}
    </span>
  );
}
